import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { CustomerReportingUsageWarningService } from '../../services/customer-reporting-usage-warning.service';
import { Network, ConnectionStatus } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';
import { PluginListenerHandle } from '@capacitor/core/types/definitions';

@Component({
  selector: 'app-data-usage-warning',
  templateUrl: './data-usage-warning.component.html',
  styleUrls: ['./data-usage-warning.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataUsageWarningComponent implements OnInit, OnDestroy {
  dontShowAgain = false;
  isDismissed = false;
  isHidden = true;
  private networkListener: PluginListenerHandle;
  private webNetworkChangeListener: () => void;

  constructor(
    private customerReportingUsageWarningService: CustomerReportingUsageWarningService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
  ) {}

  async ngOnInit(): Promise<void> {
    const isWarningHidden =
      this.customerReportingUsageWarningService.currentWarning;
    // Get initial network status and listen for changes
    if (Capacitor.isNativePlatform()) {
      const initialStatus = await Network.getStatus();
      this.updateVisibility(initialStatus, isWarningHidden);

      this.networkListener = await Network.addListener(
        'networkStatusChange',
        status => {
          this.zone.run(() => {
            this.updateVisibility(
              status,
              this.customerReportingUsageWarningService.currentWarning,
            );
          });
        },
      );
    } else {
      // Fallback for web with change listener
      this.webNetworkChangeListener = () => {
        // Run inside the Angular zone to ensure change detection is triggered
        this.zone.run(() => {
          const isOnline = navigator.onLine;
          const connection = (navigator as any).connection;

          let connectionType: 'cellular' | 'wifi' = 'wifi'; // Default to wifi
          if (connection) {
            if (connection.type) {
              connectionType =
                connection.type === 'cellular' ? 'cellular' : 'wifi';
            } else {
              connectionType = connection.rtt === 200 ? 'wifi' : 'cellular';
            }
          }

          this.updateVisibility(
            { connected: isOnline, connectionType: connectionType },
            this.customerReportingUsageWarningService.currentWarning,
          );
        });
      };
      this.webNetworkChangeListener();

      if ((navigator as any).connection) {
        (navigator as any).connection.addEventListener(
          'change',
          this.webNetworkChangeListener,
        );
      }
    }
  }

  ngOnDestroy(): void {
    if (this.networkListener) {
      this.networkListener.remove();
    }
    if (this.webNetworkChangeListener && (navigator as any).connection) {
      (navigator as any).connection.removeEventListener(
        'change',
        this.webNetworkChangeListener,
      );
    }
  }

  private updateVisibility(
    status: ConnectionStatus,
    isWarningHidden: boolean,
  ): void {
    const shouldShow =
      !isWarningHidden &&
      status.connected &&
      status.connectionType === 'cellular';
    this.isHidden = !shouldShow;
    this.cdr.markForCheck();
  }

  dismiss(): void {
    if (this.dontShowAgain) {
      this.customerReportingUsageWarningService.setHideWarning(true);
    }
    this.isDismissed = true;
  }

  onCheckboxChange(event: CustomEvent): void {
    this.dontShowAgain = event.detail.checked;
  }
}
